import type { z } from "zod";
import { ApiError } from "./ApiError";
import {
  type ApiEnvelope,
  envelopeSchema,
  type Paginated,
  paginatedSchema,
} from "./envelope.schemas";

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Appended to the URL; null/undefined/"" values are dropped. */
  query?: Record<string, QueryValue>;
  /** Skip the 401 -> refresh -> retry dance for this call. */
  noRefresh?: boolean;
}

/** A DELETE's options, plus a JSON body for the routes that bind one. */
export interface DeleteOptions extends RequestOptions {
  body?: unknown;
}

export interface ApiClientOptions {
  /**
   * The API's origin, or "" for same-origin through the dev proxy. Paths are appended verbatim. An
   * origin makes every call cross-origin, so the refresh cookie needs `credentials: "include"`; it is
   * still sent only from a page on the API's own site (`SameSite=Strict`, ADR-0008).
   */
  baseUrl: string;
  getAccessToken?: () => string | null | undefined;
  getTenantSlug?: () => string | null | undefined;
  /**
   * Exchange the refresh token for a new access token. Resolve null when the session is over (the
   * refresh was refused); reject when it could not be tried just now (offline, 429, 5xx), and the
   * caller gets that error instead of a sign-out.
   */
  refresh?: () => Promise<string | null>;
  /**
   * Called once a 401 that means "this session is over" could not be recovered. Never for a
   * `noRefresh` request, and never for a 401 whose code is not a token code (a wrong code or
   * password on a route that checks one).
   */
  onUnauthorized?: (error: ApiError) => void;
  fetch?: typeof globalThis.fetch;
  defaultHeaders?: Record<string, string>;
}

export interface DownloadedFile {
  blob: Blob;
  /** From Content-Disposition; null when the server sent none. */
  filename: string | null;
  headers: Headers;
}

export interface ApiClient {
  /** Low level: returns the validated envelope with `data` still unknown. */
  request(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<ApiEnvelope>;
  get<T>(schema: z.ZodType<T>, path: string, options?: RequestOptions): Promise<T>;
  post<T>(schema: z.ZodType<T>, path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(schema: z.ZodType<T>, path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  /**
   * A DELETE may carry a JSON body where the API binds one, such as the password that confirms an
   * authenticator reset (`DELETE /platform/auth/mfa/totp`).
   */
  delete<T>(schema: z.ZodType<T>, path: string, options?: DeleteOptions): Promise<T>;
  /** List endpoints: validates `data[]` against `item` and returns `{ items, pagination }`. */
  getPaginated<T>(
    item: z.ZodType<T>,
    path: string,
    options?: RequestOptions,
  ): Promise<Paginated<T>>;
  /**
   * A file rather than an envelope (the audit CSV export). Same headers, refresh and error envelope as
   * every other call; on success the body is returned untouched with the server's filename, if any.
   */
  getBlob(path: string, options?: RequestOptions): Promise<DownloadedFile>;
}

/**
 * The endpoints that must never trigger a refresh of their own, or a 401 from one of them would
 * refresh, fail, and be reported as the session ending. `customer-auth` is the booking app's copy of
 * the same three routes, and leaving it out made a customer's expired session look like a signed-in
 * one that had suddenly gone wrong.
 */
const AUTH_PATH = /\/(?:customer-)?auth\/(login|refresh|logout)\b/;

/**
 * The codes of a 401 that means the access or refresh token is no good, the only 401 worth a
 * refresh or a sign-out. `http.401` is a 401 with no readable envelope, which can only be the
 * bearer handler's. Any other 401 code (a wrong second-factor code, say) is an answer about the
 * request, not the session: treating it as an expiry used to refresh and then sign the user out of
 * every tab for a typo.
 */
const TOKEN_FAILURE_CODES: ReadonlySet<string> = new Set([
  "auth.invalid_token",
  "auth.unauthenticated",
  "auth.invalid_refresh_token",
  "auth.refresh_token_reused",
  "identity.customer_invalid_refresh_token",
  "http.401",
]);

export function isTokenFailure(error: ApiError): boolean {
  return error.statusCode === 401 && TOKEN_FAILURE_CODES.has(error.code);
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  // Resolved per call, not at construction: test tools and polyfills replace globalThis.fetch later.
  const fetchImpl: typeof globalThis.fetch =
    options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  let refreshInFlight: Promise<string | null> | null = null;
  // Cross-origin when the API has its own origin: the browser then sends and stores the refresh
  // cookie only with "include" (ADR-0008). Same-origin behind the dev proxy, as before.
  const credentials: RequestCredentials = options.baseUrl ? "include" : "same-origin";

  /**
   * Single flight: every 401 that lands while a refresh runs waits for that same promise. A
   * rejection is passed on rather than read as "signed out": it means the refresh could not be
   * tried (offline, throttled, server error) and the session is still there.
   */
  function refreshOnce(): Promise<string | null> {
    if (!options.refresh) return Promise.resolve(null);
    refreshInFlight ??= options.refresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  /** Refreshes for a request, turning a failure to try into an error for that request alone. */
  async function refreshForRequest(): Promise<string | null> {
    try {
      return await refreshOnce();
    } catch (cause) {
      if (cause instanceof ApiError) throw cause;
      throw new ApiError({
        statusCode: 0,
        code: "network.unreachable",
        message: "Could not reach the server.",
        cause,
      });
    }
  }

  /** Whether this 401 is the session ending, which only a request that may refresh can say. */
  function sessionEnded(error: ApiError, path: string, requestOptions: RequestOptions): boolean {
    return isTokenFailure(error) && !requestOptions.noRefresh && !AUTH_PATH.test(path);
  }

  function buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const url = /^https?:\/\//.test(path) ? path : `${options.baseUrl}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === "") continue;
      params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
  }

  function buildHeaders(
    body: unknown,
    extra?: Record<string, string>,
    tokenOverride?: string,
  ): Headers {
    const headers = new Headers(options.defaultHeaders);
    headers.set("Accept", "application/json");
    // A FormData body sets its own multipart Content-Type, boundary included, when fetch sends it;
    // setting one here would drop the boundary and the server could not read the parts.
    if (body !== undefined && !(body instanceof FormData))
      headers.set("Content-Type", "application/json");
    const tenant = options.getTenantSlug?.();
    if (tenant) headers.set("X-Tenant", tenant);
    const token = tokenOverride ?? options.getAccessToken?.();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (extra) for (const [key, value] of Object.entries(extra)) headers.set(key, value);
    return headers;
  }

  async function send(
    method: string,
    path: string,
    body: unknown,
    requestOptions: RequestOptions,
    retryToken?: string,
  ): Promise<ApiEnvelope> {
    const url = buildUrl(path, requestOptions.query);
    let response: Response;
    try {
      response = await fetchImpl(url, {
        method,
        headers: buildHeaders(body, requestOptions.headers, retryToken),
        body:
          body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
        credentials,
        signal: requestOptions.signal,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new ApiError({
        statusCode: 0,
        code: "network.unreachable",
        message: "Could not reach the server.",
        cause,
      });
    }

    const envelope = await readEnvelope(response, method, path);

    if (!response.ok || !envelope.success) {
      const error = ApiError.fromEnvelope(envelope, response.status);
      const ended = sessionEnded(error, path, requestOptions);

      if (ended && retryToken === undefined && options.refresh !== undefined) {
        const token = await refreshForRequest();
        if (token) return send(method, path, body, requestOptions, token);
      }

      if (ended) options.onUnauthorized?.(error);
      throw error;
    }
    return envelope;
  }

  async function download(
    path: string,
    requestOptions: RequestOptions,
    retryToken?: string,
  ): Promise<DownloadedFile> {
    let response: Response;
    try {
      const headers = buildHeaders(undefined, requestOptions.headers, retryToken);
      headers.set("Accept", "*/*");
      response = await fetchImpl(buildUrl(path, requestOptions.query), {
        method: "GET",
        headers,
        credentials,
        signal: requestOptions.signal,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new ApiError({
        statusCode: 0,
        code: "network.unreachable",
        message: "Could not reach the server.",
        cause,
      });
    }

    if (response.ok) {
      return {
        blob: await response.blob(),
        filename: filenameOf(response.headers.get("Content-Disposition")),
        headers: response.headers,
      };
    }

    // A refusal is an ordinary envelope, so it reads and refreshes exactly like any other call.
    const envelope = await readEnvelope(response, "GET", path);
    const error = ApiError.fromEnvelope(envelope, response.status);
    const ended = sessionEnded(error, path, requestOptions);
    if (ended && retryToken === undefined && options.refresh) {
      const token = await refreshForRequest();
      if (token) return download(path, requestOptions, token);
    }
    if (ended) options.onUnauthorized?.(error);
    throw error;
  }

  function validate<T>(schema: z.ZodType<T>, value: unknown, method: string, path: string): T {
    const result = schema.safeParse(value);
    if (result.success) return result.data;
    throw new ApiError({
      statusCode: 200,
      code: "client.invalid_response",
      message: `The response from ${method} ${path} did not match the expected shape.`,
      errors: result.error.issues.map((issue) => ({
        code: "validation.shape",
        message: issue.message,
        field: issue.path.map(String).join("."),
      })),
    });
  }

  const client: ApiClient = {
    request: (method, path, body, requestOptions = {}) => send(method, path, body, requestOptions),

    async get(schema, path, requestOptions = {}) {
      const envelope = await send("GET", path, undefined, requestOptions);
      return validate(schema, envelope.data, "GET", path);
    },

    async post(schema, path, body, requestOptions = {}) {
      const envelope = await send("POST", path, body, requestOptions);
      return validate(schema, envelope.data, "POST", path);
    },

    async put(schema, path, body, requestOptions = {}) {
      const envelope = await send("PUT", path, body, requestOptions);
      return validate(schema, envelope.data, "PUT", path);
    },

    async delete(schema, path, { body, ...requestOptions } = {}) {
      const envelope = await send("DELETE", path, body, requestOptions);
      return validate(schema, envelope.data, "DELETE", path);
    },

    async getPaginated(item, path, requestOptions = {}) {
      const envelope = await send("GET", path, undefined, requestOptions);
      const page = validate(paginatedSchema(item), envelope, "GET", path);
      return { items: page.data, pagination: page.pagination };
    },

    async getBlob(path, requestOptions = {}) {
      return download(path, requestOptions);
    },
  };

  return client;
}

async function readEnvelope(
  response: Response,
  method: string,
  path: string,
): Promise<ApiEnvelope> {
  const text = response.status === 204 ? "" : await response.text();
  if (text.trim() === "") {
    return { success: response.ok, statusCode: response.status, data: null };
  }

  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (cause) {
    throw new ApiError({
      statusCode: response.status,
      code: response.ok ? "client.invalid_response" : `http.${response.status}`,
      message: response.ok
        ? `The response from ${method} ${path} was not JSON.`
        : `Request failed with status ${response.status}.`,
      cause,
    });
  }

  const parsed = envelopeSchema.safeParse(json);
  if (parsed.success) return parsed.data;

  if (!response.ok) {
    return { success: false, statusCode: response.status, data: null };
  }
  throw new ApiError({
    statusCode: response.status,
    code: "client.invalid_response",
    message: `The response from ${method} ${path} was not a valid API envelope.`,
    cause: parsed.error,
  });
}

/** `attachment; filename="audit.csv"; filename*=UTF-8''audit.csv` → `audit.csv`. */
function filenameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() ?? null;
}
